/**
 * One dose, in the two states the fast-entry screen has:
 *
 *  - PENDING: name + meta, three time chips, an optional typed-time row, and
 *    the two actions (Donné à HH:MM / Non donné -> reason chips).
 *  - SAVED: a compact row with a status badge and Modifier.
 *
 * The common case — a dose given at its scheduled time — is one tap on the
 * primary button, because the "Prévue" chip is selected by default.
 *
 * This component is presentational: it owns the chip selection and the typed
 * value, and hands a finished 'HH:mm' (or a reason) to its parent. It never
 * talks to the network, which is what makes the optimistic save and undo in
 * DoseList simple.
 */
import { useEffect, useRef, useState } from 'react';
import { parseClock, shiftClock, type DoseReason } from '@toxofollow/shared';
import { useI18n } from '../../lib/i18n.js';

export interface DoseItem {
  orderId: string;
  concept: string;
  scheduledTime: string;
  doseAmount: string | null;
  doseUnit: string | null;
  administration: { id: string; status: string; actualTime: string | null } | null;
}

type Mode = 'scheduled' | 'now' | 'custom';
const REASONS: DoseReason[] = ['refused', 'forgotten', 'vomited', 'partial', 'other'];

const CHIP_ON = 'bg-[#E6F1F5] border-[1.5px] border-brand text-[#14485A] font-[650]';
const CHIP_OFF = 'bg-white border-[1.5px] border-[#D3D9DF] text-[#2A3540] font-medium';
const CHIP_BASE = 'min-h-[44px] rounded-full px-4 text-[15px] tabular-nums';

function badgeClass(status: string): string {
  return status === 'taken' || status === 'late'
    ? 'bg-[#E4F4E8] text-[#1C6331]'
    : 'bg-[#FDF0E1] text-[#7A420C]';
}

export function DoseCard({
  item,
  now,
  busy,
  editing,
  onGive,
  onReason,
  onEdit,
}: {
  item: DoseItem;
  now: string;
  busy: boolean;
  editing: boolean;
  onGive: (time: string) => void;
  onReason: (reason: DoseReason) => void;
  onEdit: () => void;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode>(editing ? 'custom' : 'scheduled');
  const [custom, setCustom] = useState(editing ? (item.administration?.actualTime ?? item.scheduledTime) : '');
  const [skipOpen, setSkipOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (mode === 'custom') inputRef.current?.focus();
  }, [mode]);

  const meta = item.doseAmount
    ? t('entry.scheduledMetaDose', {
        time: item.scheduledTime,
        dose: `${item.doseAmount} ${item.doseUnit ?? ''}`.trim(),
      })
    : t('entry.scheduledMeta', { time: item.scheduledTime });

  // --- saved row -----------------------------------------------------------
  if (item.administration && !editing) {
    const a = item.administration;
    const label =
      a.status === 'taken' || a.status === 'late'
        ? `${t(`admin.${a.status}`)}${a.actualTime ? ` · ${a.actualTime}` : ''}`
        : t(`admin.${a.status}`);
    return (
      <article className="flex flex-wrap items-center gap-3 rounded-[14px] border border-[#E1E5EA] bg-white py-3 pl-5 pr-3">
        <div className="flex min-w-0 flex-1 basis-[200px] flex-col gap-0.5">
          <h3 className="m-0 text-[17px] font-[650]">{t(`concept.${item.concept}`)}</h3>
          <span className="text-sm tabular-nums text-[#56616D]">{meta}</span>
        </div>
        <span className={`rounded-lg px-3 py-1.5 text-[15px] font-semibold tabular-nums ${badgeClass(a.status)}`}>
          {label}
        </span>
        <button
          type="button"
          onClick={onEdit}
          className="min-h-[44px] rounded-lg px-3.5 text-[15px] font-semibold text-brand-dark underline"
        >
          {t('common.edit')}
        </button>
      </article>
    );
  }

  // --- pending card --------------------------------------------------------
  const chosen = mode === 'scheduled' ? item.scheduledTime : mode === 'now' ? now : parseClock(custom);
  const invalid = !chosen;

  let hint = '';
  let hintCls = 'text-[#56616D]';
  if (mode === 'custom') {
    if (!custom.trim()) hint = t('entry.hintEmpty');
    else if (invalid) {
      hint = t('entry.hintInvalid');
      hintCls = 'text-[#B42318] font-semibold';
    } else hint = t('entry.hintValid', { time: chosen });
  }

  // ±15 steps from the typed value, or from the scheduled time when invalid.
  const step = (delta: number) => () => setCustom(shiftClock(parseClock(custom) ?? item.scheduledTime, delta));
  const give = () => {
    if (chosen && !busy) onGive(chosen);
  };

  return (
    <article className="flex flex-col gap-3.5 rounded-[14px] border border-[#E1E5EA] bg-white px-5 py-[18px]">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="m-0 text-[19px] font-[650]">{t(`concept.${item.concept}`)}</h3>
        <span className="text-[15px] tabular-nums text-[#56616D]">{meta}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={mode === 'scheduled'}
          onClick={() => setMode('scheduled')}
          className={`${CHIP_BASE} ${mode === 'scheduled' ? CHIP_ON : CHIP_OFF}`}
        >
          {t('entry.chipScheduled', { time: item.scheduledTime })}
        </button>
        <button
          type="button"
          aria-pressed={mode === 'now'}
          onClick={() => setMode('now')}
          className={`${CHIP_BASE} ${mode === 'now' ? CHIP_ON : CHIP_OFF}`}
        >
          {t('entry.chipNow', { time: now })}
        </button>
        <button
          type="button"
          aria-pressed={mode === 'custom'}
          onClick={() => {
            setCustom(custom || (mode === 'now' ? now : item.scheduledTime));
            setMode('custom');
          }}
          className={`${CHIP_BASE} ${mode === 'custom' ? CHIP_ON : CHIP_OFF}`}
        >
          {t('entry.chipCustom')}
        </button>
      </div>

      {mode === 'custom' && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`t-${item.orderId}-${item.scheduledTime}`} className="text-sm font-semibold text-[#3A4652]">
            {t('entry.exactTime')}
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              aria-label={t('entry.minus15')}
              onClick={step(-15)}
              className="h-12 min-w-[56px] rounded-lg border border-[#D3D9DF] bg-[#F6F7F9] text-[15px] font-semibold"
            >
              −15
            </button>
            <input
              id={`t-${item.orderId}-${item.scheduledTime}`}
              ref={inputRef}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  give();
                } else if (e.key === 'Escape') {
                  setMode('scheduled');
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setCustom(shiftClock(parseClock(custom) ?? item.scheduledTime, 5));
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setCustom(shiftClock(parseClock(custom) ?? item.scheduledTime, -5));
                }
              }}
              inputMode="numeric"
              autoComplete="off"
              placeholder="hh:mm"
              className="h-12 w-28 rounded-lg border-[1.5px] border-[#8A96A3] bg-white text-center text-[22px] font-semibold tabular-nums"
            />
            <button
              type="button"
              aria-label={t('entry.plus15')}
              onClick={step(15)}
              className="h-12 min-w-[56px] rounded-lg border border-[#D3D9DF] bg-[#F6F7F9] text-[15px] font-semibold"
            >
              +15
            </button>
            <span className={`text-sm ${hintCls}`}>{hint}</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2.5">
        <button
          type="button"
          aria-expanded={skipOpen}
          onClick={() => setSkipOpen((v) => !v)}
          className="min-h-[48px] rounded-lg border border-[#D3D9DF] bg-white px-[18px] text-base font-semibold text-[#3A4652]"
        >
          {t('entry.notGiven')}
        </button>
        <button
          type="button"
          onClick={give}
          disabled={invalid || busy}
          className={`flex min-h-[48px] items-center gap-2 rounded-lg px-[22px] text-[17px] font-[650] tabular-nums text-white ${
            invalid || busy ? 'cursor-not-allowed bg-[#AEB8C2]' : 'bg-brand'
          }`}
        >
          <span aria-hidden="true">✓</span>
          {invalid ? t('entry.give') : t('entry.giveAt', { time: chosen })}
        </button>
      </div>

      {skipOpen && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="text-sm text-[#56616D]">{t('entry.reasonLabel')}</span>
          {REASONS.map((r) => (
            <button
              key={r}
              type="button"
              disabled={busy}
              onClick={() => onReason(r)}
              className="min-h-[44px] rounded-full border-[1.5px] border-[#E2C9A8] bg-[#FDF6EC] px-4 text-[15px] font-semibold text-[#7A420C] disabled:opacity-50"
            >
              {t(`reason.${r}`)}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}
